import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import useLegalCaseParties from '../../../../hooks/useLegalCaseParties';
import type { Party } from '../../../../types/LegalCase';
import LegalCasePartyForm, { type AddPartyFormValue } from './LegalCasePartyForm';
import ConfirmDialog from './ConfirmDialog';
import {
    displayPartyDocument as displayDocument,
    displayPartyName as displayName,
} from './partyDisplay';
import { namesDiffer } from './debtorSummary';

interface LegalCasePartiesProps {
    caseId: string;
    initialParties?: Party[];
}

// Partes de um caso, dentro do painel de detalhe: lista com remoção e o formulário de
// adição (LegalCasePartyForm), que resolve o personId por documento ou aceita contraparte
// externa. A regra anti-duplicação vive no backend — a tela só oferece os dois caminhos.
const LegalCaseParties = ({ caseId, initialParties }: LegalCasePartiesProps) => {
    const {
        parties, isSaving, errorFields, roleOptions, resolvedPeople,
        addParty, removeParty, clearErrors,
    } = useLegalCaseParties(caseId, initialParties);
    const [showForm, setShowForm] = useState(false);
    // Id da parte pendente de confirmação de remoção (null = diálogo fechado).
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openForm = () => {
        clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        clearErrors();
        setShowForm(false);
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await removeParty(pendingRemoval);
        setPendingRemoval(null);
    };

    const handleSubmit = async (value: AddPartyFormValue): Promise<boolean> => {
        const ok = await addParty(value);
        if (ok) closeForm();
        return ok;
    };

    return (
        <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Partes
                </h4>
                {!showForm && (
                    <button
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Adicionar
                    </button>
                )}
            </div>

            {parties.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma parte cadastrada</p>
            )}

            <ul className="space-y-2">
                {parties.map((party) => {
                    const resolved = party.personId ? resolvedPeople[party.personId] : undefined;
                    const document = displayDocument(party, resolved);
                    return (
                        <li
                            key={party.id}
                            className="flex items-start justify-between gap-2 rounded-lg bg-gray-50 dark:bg-slate-900 px-3 py-2"
                        >
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                        {party.roleFormatted}
                                    </span>
                                    <span className="text-sm dark:text-white truncate">
                                        {displayName(party, resolved)}
                                    </span>
                                </div>
                                {document && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                                        {document}
                                    </p>
                                )}
                                {party.personId && resolved && namesDiffer(party.name, resolved.name) && (
                                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                        Nome atual em Pessoas: {resolved.name}
                                    </p>
                                )}
                                {party.personId && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        Cadastro em Pessoas vinculado
                                    </p>
                                )}
                                {party.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {party.description}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setPendingRemoval(party.id)}
                                disabled={isSaving}
                                title="Remover parte"
                                className="flex-shrink-0 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                            >
                                <FaTrash size={11} />
                            </button>
                        </li>
                    );
                })}
            </ul>

            {showForm && (
                <LegalCasePartyForm
                    roleOptions={roleOptions}
                    errorFields={errorFields}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover parte"
                message="Remover esta parte do caso? Esta ação não pode ser desfeita."
                isBusy={isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseParties;
